import { assertValue } from '@shared/helpers'
import { IfpApplicationAction } from '@shared/sec-loans'
import { ShowModalOptions } from '@ui-common/components/Modal'
import { getSummaryConsentModalOptions } from '@ui-common/components/Modal/SummaryConsentModal'
import { useGlobalServices } from '@ui-common/hooks'
import { useActor } from '@ui-common/hooks/useActor'
import { CONSENT, statusStepMachine } from '@ui-common/state/statusStepMachine/statusStepMachine'
import { ModalVisualMode } from '@ui-components/containers/ModalTypes'
import { BodyText } from '@ui-components/typography/Typography'
import { useSelector } from '@xstate/react'
import { useCallback, useEffect } from 'react'
import { useIntl } from 'react-intl'

import { useIfpAnalyticsService } from '../../analytics/analyticsContext'
import { useIfpFormContext } from '../formContext'
import { Consent } from './Consent/Consent'

export const useStatusStepLogic = () => {
  const intl = useIntl()
  const { isWizardComplete, wizardData, submit, isPrimaryApplicant, onReturnApplication } = useIfpFormContext()
  const { analyticsService } = useIfpAnalyticsService()
  const { openModal, closeModal } = useGlobalServices()

  const machine = statusStepMachine({
    proceed: () => {
      submit(isPrimaryApplicant ? IfpApplicationAction.Submit : IfpApplicationAction.Accept)
    },
  })

  const actor = useActor(machine, {
    input: {
      hasConsent: false,
    },
  })

  const currentState = useSelector(actor, ({ value }) => value)

  const proceedToConsent = useCallback(() => {
    analyticsService.sendModalViewEvent('ApplicationConsentModal')
    actor.send({ type: 'PROCEED' })
  }, [actor, analyticsService])

  useEffect(() => {
    if (isWizardComplete) {
      actor.send({ type: 'COMPLETED' })
    }
  }, [actor, isWizardComplete])

  useEffect(() => {
    const confirmConsent = () => {
      analyticsService.sendCustomEvent('ApplicationConsentModal', 'ConfirmButton')
      actor.send({ type: 'CONSENT_GIVEN' })
      closeModal()
    }

    const cancelConsent = () => {
      analyticsService.sendCustomEvent('ApplicationConsentModal', 'CancelButton')
      actor.send({ type: 'CANCEL_CONSENT' })
      closeModal()
    }

    if (currentState === CONSENT) {
      openModal(
        getSummaryConsentModalOptions({
          content: (
            <Consent
              confirmConsent={confirmConsent}
              cancelConsent={cancelConsent}
              wizardData={assertValue(wizardData, 'wizardData')}
            />
          ),
          intl,
        }),
      )
    }
  }, [currentState, openModal, intl, actor, closeModal, wizardData, analyticsService])

  const openReturnApplicationModal = () => {
    const modalAnalyticsName = 'ApplicationReturnModal'
    const openReturnModalEvent: ShowModalOptions = {
      header: intl.formatMessage({ id: 'ifp-application-return-application-modal-header' }),
      children: (
        <BodyText>{intl.formatMessage({ id: 'ifp-application-return-application-modal-description' })}</BodyText>
      ),
      secondaryButtonOptions: {
        label: intl.formatMessage({ id: 'ifp-application-return-application-modal-return-button-label' }),
        onClick: () => {
          analyticsService.sendCustomEvent(modalAnalyticsName, 'ConfirmButton')
          onReturnApplication()
        },
      },
      primaryButtonOptions: {
        label: intl.formatMessage({ id: 'common-cancel' }),
        onClick: () => {
          analyticsService.sendCustomEvent(modalAnalyticsName, 'CancelButton')
        },
      },
      mode: ModalVisualMode.Help,
    }

    analyticsService.sendModalViewEvent(modalAnalyticsName)
    openModal(openReturnModalEvent)
  }

  return {
    proceedToConsent,
    openReturnApplicationModal,
  }
}
